import React, { useState, useEffect } from "react";
import { Images } from "lucide-react";
import galleryService from "./services/galleryService";

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadDynamicGallery() {
      try {
        setLoading(true);
        const liveItems = await galleryService.getPublished();
        if (liveItems && liveItems.length > 0) {
          const mapped = liveItems.map((item) => ({
            id: item.id,
            title: item.title || "Gallery Showcase",
            category: item.category || "Design",
            image: item.image_url,
          }));
          setGalleryItems(mapped);
        } else {
          setGalleryItems([]);
        }
      } catch (err) {
        console.error("Error loading gallery from Supabase:", err);
        setGalleryItems([]);
      } finally {
        setLoading(false);
      }
    }
    loadDynamicGallery();
  }, []);

  // Helper component to render clean cards for the marquee
  const renderCard = (item, uniqueKey) => (
    <div
      key={uniqueKey}
      onClick={() => setSelectedImage(item)}
      className="
        relative 
        w-[240px] sm:w-[280px] 
        flex-shrink-0 
        bg-white 
        rounded-2xl 
        border border-slate-200/80
        shadow-sm
        hover:shadow-xl 
        hover:-translate-y-1
        transition-all 
        duration-300 
        cursor-pointer
        group
        p-3.5
        flex flex-col items-center justify-between
      "
    >
      <div className="w-full h-[190px] sm:h-[220px] flex items-center justify-center overflow-hidden rounded-xl bg-slate-50 relative">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      <div className="w-full text-center mt-3 pt-1">
        <h3 className="text-sm font-bold text-slate-800 tracking-wide truncate group-hover:text-[#9BB7A3] transition-colors duration-300">
          {item.title}
        </h3>
        <p className="text-[11px] font-bold text-[#9BB7A3] uppercase tracking-wider mt-1">
          {item.category || "Explore Design"}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <section
        id="gallery"
        className="relative overflow-hidden py-24 bg-gradient-to-b from-slate-50 to-white"
      >
        {/* Decorative Background Glows */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#9BB7A3]/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#9BB7A3]/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-full mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 px-6">
            <span className="text-[#9BB7A3] font-bold tracking-[4px] uppercase text-xs sm:text-sm block">
              Studio Gallery
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 mt-3">
              Glimpse Of Our Masterpieces
            </h2>
            <div className="w-20 h-1 bg-[#9BB7A3] mx-auto mt-4 rounded-full"></div>
          </div>

          {loading ? (
            <div className="py-20 flex justify-center">
              <div className="w-10 h-10 border-3 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
            </div>
          ) : galleryItems.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 p-8 max-w-md mx-auto shadow-sm">
              <div className="w-16 h-16 rounded-2xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center mx-auto mb-4">
                <Images size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-800">No Gallery Items Published</h3>
              <p className="text-xs text-slate-500 mt-1">
                Upload images from the admin panel to showcase your designs here.
              </p>
            </div>
          ) : (
            /* Infinite Horizontal Scroll Track */
            <div className="relative w-full overflow-hidden py-4 group/track">
              {/* Soft Edge Masks */}
              <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-slate-50 via-slate-50/50 to-transparent z-20 pointer-events-none"></div>
              <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-white via-white/50 to-transparent z-20 pointer-events-none"></div>

              {/* Scrolling Container */}
              <div className="flex w-max items-center gap-5 animate-infiniteScroll group-hover/track:[animation-play-state:paused]">
                {/* First Set */}
                {galleryItems.map((item, index) => renderCard(item, `track1-${index}`))}

                {/* Duplicated Set for Seamless Infinite Loop */}
                {galleryItems.map((item, index) => renderCard(item, `track2-${index}`))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Full Screen Image Modal Overlay */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-fadeIn"
          onClick={() => setSelectedImage(null)}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 text-white text-4xl font-light hover:text-gray-300 transition-colors focus:outline-none z-50"
            onClick={() => setSelectedImage(null)}
          >
            &times;
          </button>

          {/* Full Screen Image Container */}
          <div
            className="max-w-4xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
            />
            <h3 className="text-white text-lg md:text-xl font-bold mt-4 tracking-wide text-center">
              {selectedImage.title}
            </h3>
            <p className="text-slate-300 text-xs uppercase tracking-wider font-semibold mt-1">
              {selectedImage.category}
            </p>
          </div>
        </div>
      )}

      {/* Keyframe Styles */}
      <style>
        {`
          @keyframes infiniteScroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-50% - 10px));
            }
          }
          .animate-infiniteScroll {
            animation: infiniteScroll 40s linear infinite;
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fadeIn {
            animation: fadeIn 0.25s ease-out forwards;
          }
        `}
      </style>
    </>
  );
}

export default Gallery;